import React from 'react';

import { DealerPanelContentAddress } from '@/components/sections/dealer/panel/content/address';
import { DealerPanelContentGroupContact } from '@/components/sections/dealer/panel/content/group/contact';
import { DealerPanelContentGroupService } from '@/components/sections/dealer/panel/content/group/service';
import { DealerPanelContentGroupShowroom } from '@/components/sections/dealer/panel/content/group/showroom';
import type { DealerPanelContentLabelWithIconProps } from '@/components/sections/dealer/panel/content/label-with-icon';
import { Button } from '@/components/shared/buttons/button';
import { Divider } from '@/components/shared/element/divider';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { googleMapURL } from '@/components/shared/utility/location';
import type { IDealer } from '@/types/dealer/dealer';

export interface IDealerPanelContent {
  addressLabel: string;
  googleMapButtonLabel: string;
  showroom: {
    label: string;
    sell: DealerPanelContentLabelWithIconProps;
    businessHours: DealerPanelContentLabelWithIconProps;
    telephone: DealerPanelContentLabelWithIconProps;
    messenger: DealerPanelContentLabelWithIconProps;
    line: DealerPanelContentLabelWithIconProps;
  };
  serviceCenter: {
    label: string;
    service: DealerPanelContentLabelWithIconProps;
    specialService: DealerPanelContentLabelWithIconProps;
    businessHours: DealerPanelContentLabelWithIconProps;
    telephone: DealerPanelContentLabelWithIconProps;
    telephoneAppointment: DealerPanelContentLabelWithIconProps;
    line: DealerPanelContentLabelWithIconProps;
  };
}

interface DealerPanelContentProps {
  address?: string;
  dealer: IDealer;
  panel: IDealerPanelContent;
}

export const DealerPanelContent: React.FunctionComponent<
  DealerPanelContentProps
> = (props: DealerPanelContentProps) => {
  return (
    <Box paddingRight="5">
      {!!props.address && (
        <>
          <DealerPanelContentAddress
            label={props.panel.addressLabel}
            address={props.address}
          />
          <Divider css={{ my: '$space-6' }} />
        </>
      )}
      <Grid
        rowGap="6"
        css={{
          '.dealer-group-item:last-child': {
            marginBottom: '$space-6',
            hr: {
              display: 'none',
            },
          },
        }}
      >
        {!!(
          props.dealer.sale?.show_room_lcv || props.dealer?.sale?.show_room_cv
        ) && (
          <DealerPanelContentGroupShowroom
            panel={props.panel}
            dealerSale={props.dealer.sale}
          />
        )}
        {!!(
          props.dealer.aftersale?.service_lcv ||
          props.dealer.aftersale?.service_cv
        ) && (
          <DealerPanelContentGroupService
            panel={props.panel}
            dealerAfterSale={props.dealer.aftersale}
          />
        )}
        {!!(
          props.dealer.sale?.website !== '' ||
          props.dealer.sale?.fb_sale_cv ||
          props.dealer.sale?.fb_sale_lcv
        ) &&
          !!props.dealer.sale && (
            <DealerPanelContentGroupContact dealerSale={props.dealer.sale} />
          )}
      </Grid>
      <Flex justifyContent="center" css={{ my: '$space-2' }}>
        <Button
          label={props.panel.googleMapButtonLabel}
          icon="place"
          border
          href={googleMapURL({
            lat: props.dealer.sale?.lat ?? '',
            lng: props.dealer.sale?.lon ?? '',
            name: props.dealer.mirai?.name_th,
            url: props.dealer.sale?.google_map_url,
          })}
          target="_blank"
          tracking={{
            dataTrack: 'contact-dealer',
            dataTrackText: `lcv_sale_google_map`,
            dataTrackUrl: googleMapURL({
              lat: props.dealer.sale?.lat ?? '',
              lng: props.dealer.sale?.lon ?? '',
              name: props.dealer.mirai?.name_th,
              url: props.dealer.sale?.google_map_url,
            }),
            dataTrackValue: props.panel.googleMapButtonLabel,
          }}
          test={{
            dataTest: 'button_gotoGooglemap',
          }}
        />
      </Flex>
    </Box>
  );
};

DealerPanelContent.displayName = 'SectionDealerPanelContent';
